import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'wouter';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';

import { fetchCollection, productDisplayPrice, type ShopifyCollection, type ShopifyProduct } from '@/lib/shopifyApi';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { collectionUrl } from '@/lib/collectionUrl';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { FLOWER_BOX_BUYING_GUIDE } from '@/data/flowerBoxSeoContent';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import {
  CollectionFilters,
  type FilterState,
  EMPTY_FILTERS,
  hasActiveFilters,
  OKAZJE,
  KWIATY,
} from '@/components/collection/CollectionFilters';

import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ─── Design tokens ─────────────────────────────────────── */
const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const ROSE  = '#9B2335';
const LINE  = 'rgba(210,60,120,0.18)';
const MUTED = 'rgba(42,31,20,0.45)';

const SORT_OPTIONS = [
  { label: 'Polecane',          value: 'COLLECTION_DEFAULT', reverse: false },
  { label: 'Najpopularniejsze', value: 'BEST_SELLING',       reverse: false },
  { label: 'Cena ↑',            value: 'PRICE',              reverse: false },
  { label: 'Cena ↓',            value: 'PRICE',              reverse: true  },
  { label: 'Najnowsze',         value: 'CREATED',            reverse: true  },
];

/* ─── Sort dropdown ──────────────────────────────────────── */
function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => `${o.value}:${o.reverse}` === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '10px 20px', minHeight: 44,
          border: `1px solid ${open ? DARK : 'rgba(42,31,20,0.18)'}`,
          background: open ? DARK : '#fff',
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          fontWeight: 600, color: open ? '#fff' : 'rgba(42,31,20,0.6)',
          cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: open ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
        }}
      >
        <SlidersHorizontal size={13} color={open ? 'rgba(255,255,255,0.7)' : GOLD} />
        {current.label}
        <ChevronDown size={11} color={open ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${LINE}`,
          minWidth: 200, boxShadow: '0 12px 40px rgba(42,31,20,0.1)',
        }}>
          {SORT_OPTIONS.map(o => {
            const key = `${o.value}:${o.reverse}`;
            const active = value === key;
            return (
              <button key={key} onClick={() => { onChange(key); setOpen(false); }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: active ? 'rgba(210,60,120,0.07)' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                color: active ? DARK : 'rgba(42,31,20,0.7)', fontWeight: active ? 600 : 400,
                borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent',
              }}>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton ───────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="col-grid col-skeleton" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="col-skeleton-item">
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)', marginBottom: 0 }} className="animate-pulse" />
          <div style={{ padding: '20px 22px 24px', background: '#fff' }}>
            <div style={{ height: 20, background: 'rgba(210,60,120,0.07)', marginBottom: 10, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 6, width: '90%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%' }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter helpers ─────────────────────────────────────── */
// Tags are always empty in the Shopify response — all filtering uses collection handles.
function applyFilters(products: ShopifyProduct[], filters: FilterState): ShopifyProduct[] {
  return products.filter(p => {
    const price = parseFloat(p.priceRange.minVariantPrice.amount);
    const productCollections = new Set((p.collections?.nodes ?? []).map(c => c.handle));

    // Kompozycje — collection-based
    if (filters.kompozycje.length > 0) {
      const ok = filters.kompozycje.some(k => {
        const handle = FLOWER_BOX_KOMPOZYCJE.find(x => x.value === k)?.handle;
        return handle ? productCollections.has(handle) : false;
      });
      if (!ok) return false;
    }

    // Okazje — collection-based (handles from shared OKAZJE constant)
    if (filters.okazje.length > 0) {
      const ok = filters.okazje.some(o => {
        const handle = OKAZJE.find(x => x.value === o)?.handle;
        return handle ? productCollections.has(handle) : false;
      });
      if (!ok) return false;
    }

    // Kwiaty — collection-based (handles from shared KWIATY constant)
    if (filters.kwiaty.length > 0) {
      const ok = filters.kwiaty.some(k => {
        const handle = KWIATY.find(x => x.value === k)?.handle;
        return handle ? productCollections.has(handle) : false;
      });
      if (!ok) return false;
    }

    // Price range
    if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;

    return true;
  });
}

/* ─── Flower Box-specific filter config ──────────────────── */
const FLOWER_BOX_KOMPOZYCJE: { value: string; label: string; emoji: string; handle: string }[] = [
  { value: 'Jednolite', label: 'Jednolite', emoji: '🌹', handle: 'flowerbox-jednolite' },
  { value: 'Mieszane',  label: 'Mieszane',  emoji: '💐', handle: 'flowerbox-mieszane' },
];

const FLOWER_BOX_VISIBLE_SECTIONS = ['kompozycje', 'okazje', 'kwiaty', 'cena'] as const;

/* ─── Page ───────────────────────────────────────────────── */
export default function FlowerBoxyPage() {
  useSeo(PAGE_SEO.flowerBoxy);

  const [collection, setCollection] = useState<ShopifyCollection | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  usePrerenderReady(!loading, notFound ? 404 : 200);
  const [sort,       setSort]       = useState('COLLECTION_DEFAULT:false');
  const [filters,    setFilters]    = useState<FilterState>(EMPTY_FILTERS);

  useEffect(() => {
    setLoading(true); setNotFound(false); setFilters(EMPTY_FILTERS);
    const [sortKey, reverseStr] = sort.split(':');
    fetchCollection('flowerbox', { sort: sortKey, reverse: reverseStr === 'true', limit: 100 })
      .then(col => { if (!col) setNotFound(true); else setCollection(col); })
      .catch(err => {
        // Only a real null/404 response is a missing collection.
        console.warn('[FlowerBoxyPage] catalog refresh failed:', err);
      })
      .finally(() => setLoading(false));
  }, [sort]);

  const allProducts = (collection?.products.nodes ?? []).map(p => ({ ...p, tags: p.tags ?? [] }));

  const maxPrice = useMemo(() => {
    if (allProducts.length === 0) return 1000;
    return Math.ceil(Math.max(...allProducts.map(p => parseFloat(p.priceRange.minVariantPrice.amount))) / 50) * 50;
  }, [allProducts]);

  useEffect(() => {
    if (maxPrice > 0) {
      setFilters(f => ({ ...f, priceRange: [f.priceRange[0], Math.max(f.priceRange[1], maxPrice)] }));
    }
  }, [maxPrice]);

  const activeFilters = hasActiveFilters(filters, maxPrice);
  const filtered = useMemo(() => applyFilters(allProducts, filters), [allProducts, filters]);

  const availableOkazje = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return OKAZJE.filter(o => o.handle && handles.has(o.handle));
  }, [allProducts]);

  const availableKwiaty = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return KWIATY.filter(k => k.handle && handles.has(k.handle));
  }, [allProducts]);

  const breadcrumbs = [{ label: 'Flower boxy' }];

  return (
    <>
      <main style={{ background: '#fff', minHeight: '100vh' }}>
        <PageBreadcrumb crumbs={breadcrumbs} />

        {/* ── Page header ── */}
        <section style={{ background: '#fff', padding: 'clamp(48px,6vw,72px) 0 clamp(32px,4vw,48px)' }}>
          <div className="page-container">
            <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,4.5vw,3.8rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.06 }}>
              <CmsEditorialText id="editorial.FlowerBoxyPage.h1.1" value="Flower boxy " section="Wstęp i opisy" label="Nagłówek 1" /><span style={{ color: ROSE }}><CmsEditorialText id="category.accent" value="z dostawą w Lublinie" section="Wstęp i opisy" label="Wyróżniona część nagłówka" /></span>
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, lineHeight: 1.7, color: MUTED, maxWidth: 620, margin: '0 0 12px', fontStyle: 'italic' }}><CmsEditorialText id="editorial.FlowerBoxyPage.p.1" value="Kwiaty w ozdobnym pudełku na urodziny, rocznicę lub podziękowanie. Znajdź kolorystykę i formę dla bliskiej osoby, sprawdź wybrany wariant i zamów flower box z dostawą." section="Wstęp i opisy" label="Akapit 1" multiline /></p>
            {!loading && !notFound && (
              <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, margin: 0 }}>
                {allProducts.length} {allProducts.length === 1 ? 'flower box' : allProducts.length < 5 ? 'flower boxy' : 'flower boxów'}
              </p>
            )}
          </div>
        </section>

        {/* ── White section: filter bar + products ── */}
        <div className="page-container" style={{ background: '#ffffff' }}>

        {/* ── Filter bar ───────────────────────────────────── */}
        {!loading && !notFound && allProducts.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`,
          }}>
            <div style={{ padding: '14px 0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CollectionFilters
                    filters={filters}
                    onChange={setFilters}
                    maxPrice={maxPrice}
                    sortValue={sort}
                    onSortChange={setSort}
                    sortOptions={SORT_OPTIONS}
                    visibleSections={[...FLOWER_BOX_VISIBLE_SECTIONS]}
                    kompozycjeOptions={FLOWER_BOX_KOMPOZYCJE}
                    kompozycjeLabel="Rodzaj kompozycji"
                    okazjeOptions={availableOkazje}
                    kwiatyOptions={availableKwiaty}
                  />
                </div>
                <SortDropdown value={sort} onChange={setSort} />
              </div>
            </div>
          </div>
        )}

        {/* ── Products ─────────────────────────────────────── */}
        <div style={{
          padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)',
        }}>
          {loading && <SkeletonGrid />}

          {!loading && notFound && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 16 }}>{"Kolekcja nie istnieje"}</p>
              <Link href="/" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', color: GOLD, textDecoration: 'none', textTransform: 'uppercase' }}>
                Wróć na stronę główną →
              </Link>
            </div>
          )}

          {!loading && !notFound && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK, marginBottom: 8 }}>
                {activeFilters ? 'Brak produktów spełniających filtry' : 'Brak produktów'}
              </p>
              {activeFilters && (
                <button
                  onClick={() => setFilters({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] })}
                  style={{
                    fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                    color: GOLD, background: 'none', border: 'none', cursor: 'pointer',
                    textTransform: 'uppercase', textDecoration: 'underline',
                  }}
                >
                  Wyczyść filtry
                </button>
              )}
            </div>
          )}

          <style>{`
            .col-grid { grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 768px)  { .col-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          `}</style>

          {!loading && filtered.length > 0 && (
            <div className="col-grid" style={{
              display: 'grid',
              gap: 'clamp(12px,1.5vw,24px)',
            }}>
              {filtered.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={{ label: 'Flower Boxy', href: collectionUrl('flowerbox') }}
                  centerInfo
                />
              ))}
            </div>
          )}
        </div>
        </div>{/* end white section */}

        <CategoryBuyingGuide content={FLOWER_BOX_BUYING_GUIDE} />
      </main>
      <Footer />
    </>
  );
}
